import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { get, post } from '../../utils/api'
import { IconBuilding, IconCash, IconChartBar, IconCalendarEvent, IconArrowRight } from '@tabler/icons-react'

function formatCurrency(v) { return '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) }

export default function CorporateDashboard() {
  const { user, enterHotel } = useAuth()
  const navigate = useNavigate()
  const [overview, setOverview] = useState(null)
  const [hotels, setHotels] = useState([])
  const [pipeline, setPipeline] = useState([])
  const [regions, setRegions] = useState([])
  const [regionFilter, setRegionFilter] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      get('/corporate/overview'),
      get('/corporate/hotels'),
      get('/corporate/pipeline'),
      get('/corporate/regions'),
    ]).then(([ov, ht, pl, rg]) => {
      setOverview(ov)
      setHotels(ht)
      setPipeline(pl)
      setRegions(rg)
    }).catch(console.error).finally(() => setLoading(false))
  }, [])

  const handleDrillIn = async (hotelId) => {
    try {
      const hotel = await post(`/corporate/drill-in/${hotelId}`)
      enterHotel(hotel)
      navigate('/corporate/hotel-view')
    } catch (err) { console.error(err) }
  }

  const filtered = regionFilter ? hotels.filter(h => String(h.region_id) === regionFilter) : hotels

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>

  const kpis = [
    { label: 'Properties', value: overview?.hotel_count || 0, icon: IconBuilding, color: 'bg-blue-lt' },
    { label: 'YTD Revenue', value: formatCurrency(overview?.ytd_revenue), icon: IconCash, color: 'bg-green-lt' },
    { label: 'Active Pipeline', value: formatCurrency(overview?.pipeline_value), icon: IconChartBar, color: 'bg-yellow-lt' },
    { label: 'Upcoming Events', value: overview?.upcoming_events || 0, icon: IconCalendarEvent, color: 'bg-purple-lt' },
  ]

  return (
    <div>
      <div className="mb-4">
        <h2 className="mb-1">Portfolio Dashboard</h2>
        <div className="text-secondary">Welcome back, {user?.name}</div>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        {kpis.map(k => (
          <div key={k.label} className="col-sm-6 col-lg-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-secondary text-uppercase" style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em' }}>{k.label}</div>
                    <div className="h2 mb-0 mt-1">{k.value}</div>
                  </div>
                  <div className={`avatar avatar-lg ${k.color} rounded`}><k.icon size={24} /></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        {/* Hotel Performance Table */}
        <div className="col-lg-8">
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h3 className="card-title mb-0">Hotel Performance</h3>
              {regions.length > 1 && (
                <select className="form-select form-select-sm" style={{ width: 'auto' }} value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
                  <option value="">All Regions</option>
                  {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              )}
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>Region</th>
                    <th className="text-end">Pipeline</th>
                    <th className="text-end">YTD Revenue</th>
                    <th className="text-end">Leads</th>
                    <th className="text-end">Events</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(h => (
                    <tr key={h.id}>
                      <td>
                        <div className="fw-medium">{h.name}</div>
                        <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{h.city}, {h.state}</div>
                      </td>
                      <td>{h.region_name && <span className="badge bg-blue-lt">{h.region_name}</span>}</td>
                      <td className="text-end">{formatCurrency(h.pipeline)}</td>
                      <td className="text-end">{formatCurrency(h.ytd_revenue)}</td>
                      <td className="text-end">{h.active_leads}</td>
                      <td className="text-end">{h.upcoming_events}</td>
                      <td>
                        <button className="btn btn-sm btn-ghost-primary" onClick={() => handleDrillIn(h.id)} title="View Hotel">
                          <IconArrowRight size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && <tr><td colSpan={7} className="text-center text-secondary py-4">No hotels found</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pipeline Sidebar */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-header"><h3 className="card-title mb-0">Pipeline by Status</h3></div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush">
                {pipeline.map(p => (
                  <div key={p.stage} className="list-group-item d-flex justify-content-between align-items-center">
                    <span className="text-capitalize">{p.stage.replace(/_/g, ' ')}</span>
                    <div className="text-end">
                      <div className="fw-medium">{formatCurrency(p.total)}</div>
                      <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{p.count} deals</div>
                    </div>
                  </div>
                ))}
                {!pipeline.length && <div className="list-group-item text-center text-secondary">No pipeline data</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
